import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, X, Loader2, CheckCircle2, AlertCircle, Edit2, Save, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { CATEGORIES } from "@/lib/constants";
import { supabase } from "@/integrations/supabase/client";
import { compressImage } from "@/lib/imageCompression";
import { useRateLimit, RATE_LIMIT_PRESETS } from "@/hooks/useRateLimit";

interface BatchReceipt {
  id: string;
  file: File;
  uploadedFilePath?: string;
  merchant: string;
  total: number;
  date: string;
  category: string;
  status: 'pending' | 'processing' | 'success' | 'error';
  error?: string;
  editing?: boolean;
}

interface BatchUploadProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (receipts: BatchReceipt[]) => void;
}

export default function BatchUpload({ open, onClose, onSubmit }: BatchUploadProps) {
  const { toast } = useToast();
  const [receipts, setReceipts] = useState<BatchReceipt[]>([]);
  const [processing, setProcessing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkCategory, setBulkCategory] = useState<string>("");
  const [bulkDate, setBulkDate] = useState<string>("");
  const [bulkMerchantPrefix, setBulkMerchantPrefix] = useState<string>("");
  const { checkLimit, isLimited } = useRateLimit(RATE_LIMIT_PRESETS.batchUpload);

  const MAX_FILES = 20;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let files = Array.from(e.target.files || []);
    e.target.value = '';
    
    // Calculate how many files we can accept
    const remainingSlots = MAX_FILES - receipts.length;
    
    if (remainingSlots <= 0) {
      toast({
        title: "Maximum Limit Reached",
        description: `You already have ${MAX_FILES} receipts. Remove some to add more.`,
        variant: "destructive",
      });
      return;
    }
    
    // If user selected more than allowed, slice to the limit and notify
    if (files.length > remainingSlots) {
      toast({
        title: "File Limit Applied",
        description: `Only the first ${remainingSlots} file${remainingSlots > 1 ? 's' : ''} will be added (max ${MAX_FILES} total)`,
      });
      files = files.slice(0, remainingSlots);
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'application/pdf'];
    const invalidFiles = files.filter(f => !validTypes.includes(f.type));
    
    if (invalidFiles.length > 0) {
      toast({
        title: "Invalid Files",
        description: "Please upload only JPEG, PNG, HEIC, or PDF files",
        variant: "destructive",
      });
      return;
    }

    const newReceipts: BatchReceipt[] = files.map(file => ({
      id: crypto.randomUUID(),
      file,
      uploadedFilePath: undefined,
      merchant: '',
      total: 0,
      date: new Date().toISOString().split('T')[0],
      category: 'Other',
      status: 'processing' as const,
    }));

    setReceipts(prev => [...prev, ...newReceipts]);
    
    toast({
      title: "Processing Receipts",
      description: `Analyzing ${files.length} receipt${files.length > 1 ? 's' : ''}...`,
    });

    for (const receipt of newReceipts) {
      await processReceiptFile(receipt);
    }
  };

  const processReceiptFile = async (receipt: BatchReceipt) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error("Batch upload: not authenticated");
        updateReceipt(receipt.id, {
          merchant: receipt.merchant?.trim() ? receipt.merchant : "Unknown Merchant",
          status: "success",
          editing: true,
        });
        return;
      }

      let fileToUpload = receipt.file;
      try {
        fileToUpload = await compressImage(receipt.file, 1920, 1920, 0.7);
        console.log(`Batch upload: compressed ${receipt.file.name} from ${receipt.file.size} to ${fileToUpload.size}`);
      } catch (compressError) {
        console.warn("Batch upload: compression failed, using original file", compressError);
      }

      const fileExt = fileToUpload.name.split(".").pop() || "jpg";
      const filePath = `${user.id}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("receipts")
        .upload(filePath, fileToUpload);

      if (uploadError) {
        console.error("Batch upload: preview upload failed", uploadError);
        updateReceipt(receipt.id, {
          merchant: receipt.merchant?.trim() ? receipt.merchant : "Unknown Merchant",
          status: "success",
          editing: true,
        });
        return;
      }

      // Save the uploaded path so final submission can reuse it (avoids uploading twice)
      updateReceipt(receipt.id, { uploadedFilePath: filePath });

      const { data: parseData, error: invokeError } = await supabase.functions.invoke(
        "parse-multi-receipt",
        { body: { filePaths: [filePath] } }
      );

      if (invokeError) {
        console.error("Batch upload: AI parse error", invokeError);
        updateReceipt(receipt.id, {
          merchant: receipt.merchant?.trim() ? receipt.merchant : "Unknown Merchant",
          status: "success",
          editing: true,
        });
        return;
      }

      if (!parseData || parseData.success !== true || !parseData.data) {
        console.warn("Batch upload: AI returned no data, falling back to manual", parseData);
        updateReceipt(receipt.id, {
          merchant: receipt.merchant?.trim() ? receipt.merchant : "Unknown Merchant",
          status: "success",
          editing: true,
        });
        return;
      }

      const parsed = parseData.data;

      updateReceipt(receipt.id, {
        merchant: parsed.storeName || receipt.merchant || "Unknown Merchant",
        total: parsed.total ?? receipt.total ?? 0,
        date: parsed.date || receipt.date || new Date().toISOString().split("T")[0],
        category: receipt.category || "Other",
        status: "success",
        editing: true,
      });
    } catch (error) {
      console.error("Batch upload: unexpected error while parsing", error);
      updateReceipt(receipt.id, {
        merchant: receipt.merchant?.trim() ? receipt.merchant : "Unknown Merchant",
        status: "success",
        editing: true,
      });
    }
  };

  const removeReceipt = (id: string) => {
    setReceipts(prev => prev.filter(r => r.id !== id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const updateReceipt = (id: string, updates: Partial<BatchReceipt>) => {
    setReceipts(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  };

  const toggleEdit = (id: string) => {
    setReceipts(prev => prev.map(r => 
      r.id === id ? { ...r, editing: !r.editing } : r
    ));
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === receipts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(receipts.map(r => r.id)));
    }
  };

  const applyBulkEdit = () => {
    if (selectedIds.size === 0) {
      toast({
        title: "No Receipts Selected",
        description: "Please select at least one receipt to bulk edit",
        variant: "destructive",
      });
      return;
    }

    setReceipts(prev => prev.map(r => {
      if (!selectedIds.has(r.id)) return r;
      
      const updates: Partial<BatchReceipt> = {};
      
      if (bulkCategory) {
        updates.category = bulkCategory;
      }
      
      if (bulkDate) {
        updates.date = bulkDate;
      }
      
      if (bulkMerchantPrefix) {
        const currentMerchant = r.merchant || "";
        if (!currentMerchant.startsWith(bulkMerchantPrefix)) {
          updates.merchant = `${bulkMerchantPrefix} ${currentMerchant}`.trim();
        }
      }
      
      return { ...r, ...updates };
    }));

    const count = selectedIds.size;
    toast({
      title: "Bulk Edit Applied",
      description: `Updated ${count} receipt${count > 1 ? 's' : ''}`,
    });

    // Reset bulk edit form
    setBulkEditOpen(false);
    setBulkCategory("");
    setBulkDate("");
    setBulkMerchantPrefix("");
  };

  const handleSubmit = () => {
    // Check rate limit before batch submission
    if (!checkLimit()) {
      return;
    }
    
    const warnings: string[] = [];

    // Sanitize all receipts - ensure merchant has a value
    const sanitizedReceipts = receipts.map((r) => ({
      ...r,
      merchant: r.merchant?.trim() ? r.merchant.trim() : "Unknown Merchant",
    }));

    // Only warn about zero amounts, don't block submission
    sanitizedReceipts.forEach((r) => {
      if (r.total === 0) {
        warnings.push(`${r.file.name}: Amount is $0`);
      }
    });

    // Validate only critical fields (date format)
    const invalidDates = sanitizedReceipts.filter(r => {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      return !dateRegex.test(r.date);
    });

    if (invalidDates.length > 0) {
      toast({
        title: "Invalid Dates",
        description: `${invalidDates.length} receipt(s) have invalid date format`,
        variant: "destructive",
      });
      return;
    }

    // Show warning for zero amounts but still allow submission
    if (warnings.length > 0) {
      toast({
        title: "Review Amounts",
        description: `${warnings.length} receipt(s) have $0 amount. You can still submit or edit them first.`,
      });
    }

    onSubmit(sanitizedReceipts);
    handleClose();
  };

  const handleClose = () => {
    setReceipts([]);
    setProcessing(false);
    setSelectedIds(new Set());
    setBulkEditOpen(false);
    setBulkCategory("");
    setBulkDate("");
    setBulkMerchantPrefix("");
    onClose();
  };

  const getStatusBadge = (status: BatchReceipt['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'processing':
        return <Badge className="bg-blue-500"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>;
      case 'success':
        return <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />Ready</Badge>;
      case 'error':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Error</Badge>;
    }
  };

  const allSelected = receipts.length > 0 && selectedIds.size === receipts.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < receipts.length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Batch Upload Receipts</DialogTitle>
          <DialogDescription>
            Upload and process multiple receipts at once (up to {MAX_FILES})
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {receipts.length === 0 ? (
            <div className="border-2 border-dashed border-border rounded-lg p-12 text-center">
              <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-semibold mb-2">Upload Multiple Receipts</p>
              <p className="text-sm text-muted-foreground mb-4">
                Select up to {MAX_FILES} images or PDF files
              </p>
              <label htmlFor="batch-upload">
                <Button type="button" asChild>
                  <span className="cursor-pointer">Choose Files</span>
                </Button>
              </label>
              <input
                id="batch-upload"
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm text-muted-foreground">
                  {receipts.length} receipt{receipts.length > 1 ? 's' : ''} added
                  {selectedIds.size > 0 && ` • ${selectedIds.size} selected`}
                </p>
                <div className="flex items-center gap-2">
                  {selectedIds.size > 0 && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setBulkEditOpen(!bulkEditOpen)}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Bulk Edit ({selectedIds.size})
                    </Button>
                  )}
                  <label htmlFor="batch-upload-more">
                    <Button type="button" variant="outline" size="sm" disabled={receipts.length >= MAX_FILES} asChild>
                      <span className="cursor-pointer inline-flex items-center">
                        <Upload className="w-4 h-4 mr-2" />
                        Add More
                      </span>
                    </Button>
                  </label>
                </div>
                <input
                  id="batch-upload-more"
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Needs Amount Entry Alert */}
              {(() => {
                const needsAmountCount = receipts.filter(r => r.status === 'success' && r.total === 0).length;
                if (needsAmountCount > 0) {
                  return (
                    <div className="flex items-center gap-2 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg px-4 py-2 text-orange-700 dark:text-orange-300">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-medium">
                        {needsAmountCount} receipt{needsAmountCount > 1 ? 's need' : ' needs'} amount entry
                      </span>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Bulk Edit Toolbar */}
              {bulkEditOpen && selectedIds.size > 0 && (
                <div className="bg-muted/50 border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      Bulk Edit {selectedIds.size} Receipt{selectedIds.size > 1 ? 's' : ''}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setBulkEditOpen(false)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Category</label>
                      <Select value={bulkCategory} onValueChange={setBulkCategory}>
                        <SelectTrigger className="h-9 bg-background">
                          <SelectValue placeholder="Select category..." />
                        </SelectTrigger>
                        <SelectContent className="bg-background z-50">
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Date</label>
                      <Input
                        type="date"
                        value={bulkDate}
                        onChange={(e) => setBulkDate(e.target.value)}
                        className="h-9"
                        placeholder="Set date..."
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Merchant Prefix</label>
                      <Input
                        type="text"
                        value={bulkMerchantPrefix}
                        onChange={(e) => setBulkMerchantPrefix(e.target.value)}
                        className="h-9"
                        placeholder="e.g. Costco"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" onClick={applyBulkEdit}>
                      Apply Changes
                    </Button>
                  </div>
                </div>
              )}

              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">
                        <Checkbox
                          checked={allSelected}
                          ref={(el) => {
                            if (el) (el as any).indeterminate = someSelected;
                          }}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Select all"
                        />
                      </TableHead>
                      <TableHead className="w-[180px]">File Name</TableHead>
                      <TableHead>Merchant</TableHead>
                      <TableHead className="w-[100px]">Total ($)</TableHead>
                      <TableHead className="w-[130px]">Date</TableHead>
                      <TableHead className="w-[140px]">Category</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[90px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receipts.map((receipt) => (
                      <TableRow key={receipt.id} className={selectedIds.has(receipt.id) ? "bg-muted/30" : ""}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(receipt.id)}
                            onCheckedChange={() => toggleSelect(receipt.id)}
                            aria-label={`Select ${receipt.file.name}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-sm">
                          {receipt.file.name.substring(0, 20)}
                          {receipt.file.name.length > 20 && '...'}
                        </TableCell>
                        <TableCell>
                          {receipt.editing ? (
                            <Input
                              value={receipt.merchant}
                              onChange={(e) => updateReceipt(receipt.id, { merchant: e.target.value })}
                              placeholder="Merchant name"
                              className="h-8"
                            />
                          ) : (
                            <span className={!receipt.merchant ? 'text-muted-foreground italic' : ''}>
                              {receipt.merchant || 'Not set'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {receipt.editing ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={receipt.total || ''}
                              onChange={(e) => updateReceipt(receipt.id, { total: parseFloat(e.target.value) || 0 })}
                              placeholder="0.00"
                              className={`h-8 ${receipt.total === 0 ? 'ring-2 ring-orange-400 focus:ring-orange-500' : ''}`}
                              autoFocus={receipt.total === 0}
                            />
                          ) : (
                            <span className={receipt.total <= 0 ? 'text-muted-foreground italic' : ''}>
                              {receipt.total > 0 ? `$${receipt.total.toFixed(2)}` : 'Not set'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {receipt.editing ? (
                            <Input
                              type="date"
                              value={receipt.date}
                              onChange={(e) => updateReceipt(receipt.id, { date: e.target.value })}
                              className="h-8"
                            />
                          ) : (
                            receipt.date
                          )}
                        </TableCell>
                        <TableCell>
                          {receipt.editing ? (
                            <Select 
                              value={receipt.category} 
                              onValueChange={(value) => updateReceipt(receipt.id, { category: value })}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-background z-50">
                                {CATEGORIES.map((cat) => (
                                  <SelectItem key={cat} value={cat}>
                                    {cat}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            receipt.category
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {getStatusBadge(receipt.status)}
                            {receipt.status === 'error' && receipt.error && (
                              <p className="text-xs text-destructive truncate max-w-[100px]" title={receipt.error}>
                                {receipt.error}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => toggleEdit(receipt.id)}
                            >
                              {receipt.editing ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => removeReceipt(receipt.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>

        {receipts.length > 0 && (
          <div className="flex justify-between items-center pt-4 border-t">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={processing || isLimited()}>
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>Submit {receipts.length} Receipt{receipts.length > 1 ? 's' : ''}</>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
